/**
 * Deep-link fragment scroll — honour the URL's `#hash` on first load AND on every
 * client-side visit that carries one.
 *
 * Native "scroll to #hash on load" is unreliable in this kit: `<html>` carries
 * `scroll-behavior: smooth` (base/_reset.css) and Swup + async-Alpine islands
 * reflow the viewport just after first paint, so the browser's on-load fragment
 * jump lands at the top. (The separate scroll FREEZE — `overflow-x: clip` on the
 * root <html> — is prevented in _reset.css: horizontal-overflow containment lives
 * on <body>. This module handles the TIMING so the jump actually fires.) Harvested
 * from montgomery-homes (MOHO-40).
 *
 * ── BOTH PATHS SETTLE NOW, AND ONLY ONE USED TO ─────────────────────────────
 *
 * The initial load retried across the boot window; a Swup visit got ONE attempt
 * on `page:view` and had to be right first time. It is right first time only for
 * a target near the top of the document, which is why this looked fine for
 * months: measured on 3 Sep 2026, a cross-page jump to `/#what` — 671px down —
 * landed correctly, and the same click through to `/#approach` — 2055px down —
 * did not move the page at all. Everything between the two anchors is images
 * and a card grid, and they size AFTER `page:view` fires, so the one attempt
 * measured a document that was still short and the browser clamped the scroll.
 *
 * So both paths run the same settle: jump, then keep re-jumping across the
 * window in which the page can still grow, and stop the moment the reader
 * touches anything. A jump that is already correct re-applies the same number
 * and nothing moves.
 *
 * EACH RUN CANCELS THE ONE BEFORE IT. Without that, a reader who navigates
 * twice quickly has two loops alive and the older one drags them back to the
 * previous page's anchor. The generation counter is the whole guard.
 *
 * Pure same-page anchor CLICKS are left to the browser (the router's
 * `ignoreVisit` keeps `href="#..."` links out of Swup): the browser scrolls them
 * live with CSS smooth scrolling, honouring each target's `scroll-margin-top`.
 * `scrollIntoView` here uses `behavior: 'auto'` — an instant jump — which
 * overrides the smooth CSS for the programmatic deep-link case.
 *
 * @param {import('swup').default} [swup] the running router instance
 * @returns {void}
 */
export function initHashScroll(swup) {
    /** Bumped per run; a stale loop sees the change and stops. */
    let generation = 0;

    const targetFromHash = () => {
        const hash = window.location.hash;

        if (!hash || hash === '#swup') {
            return null;
        }

        let id;
        try {
            id = decodeURIComponent(hash.slice(1));
        } catch {
            id = hash.slice(1);
        }

        return document.getElementById(id);
    };

    /**
     * Jump to the URL's target and hold it there while the page settles.
     *
     * @returns {void}
     */
    const settleToHash = () => {
        if (!targetFromHash()) {
            return;
        }

        const run = ++generation;

        // Only GENUINE input counts — the programmatic scrollIntoView below
        // fires none of these, so the loop cannot yield to itself.
        let userTookOver = false;
        const yieldToUser = () => {
            userTookOver = true;
        };
        const opts = { passive: true, once: true };
        const events = ['wheel', 'touchmove', 'keydown'];
        events.forEach((evt) => window.addEventListener(evt, yieldToUser, opts));

        let attempts = 0;
        const step = () => {
            if (userTookOver || run !== generation) {
                events.forEach((evt) => window.removeEventListener(evt, yieldToUser, opts));
                return;
            }

            targetFromHash()?.scrollIntoView({ behavior: 'auto' });

            if (attempts++ < 30) {
                setTimeout(step, 80);
                return;
            }

            events.forEach((evt) => window.removeEventListener(evt, yieldToUser, opts));
        };

        requestAnimationFrame(step);
    };

    // Every client-side visit that carries a hash. No-ops on hashless ones.
    swup?.hooks?.on('page:view', settleToHash);

    // And the initial server-rendered load, where Swup's page:view never fires.
    settleToHash();
}
